import { api } from '@/lib/api';
import type { Answers, AnswerValue, Catalogue } from './catalogue';

export type ApplicationStatus = 'DRAFT' | 'SUBMITTED' | 'IN_REVIEW' | 'INFORMATION_REQUESTED' | 'CLOSED';

/** An application as its applicant sees it: GET /api/applications/current. */
export interface ApplicationView {
  applicationId: string;
  reference: string | null;
  status: ApplicationStatus;
  currentStep: number;
  catalogueVersion: number;
  answers: Answers;
  version: number;
  consentedAt: string | null;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  /** Whether gaps stop it being sent: SUBMISSION_MODE on the server. */
  submissionMode: 'lenient' | 'strict';
  /** While the team has asked for more: what they asked, and when. */
  informationRequest: { reason: string; requestedAt: string } | null;
}

/** Changed answers only; null removes one. */
export type AnswerChanges = Readonly<Record<string, AnswerValue | null>>;

export interface SaveRequest {
  version: number;
  currentStep: number;
  answers: AnswerChanges;
  consent?: boolean;
}

let catalogue: Promise<Catalogue> | null = null;

export const intake = {
  /** The questionnaire changes only with a release, so one fetch per page load. */
  catalogue(): Promise<Catalogue> {
    catalogue ??= api.get<Catalogue>('/api/catalogue').catch((error: unknown) => {
      catalogue = null;
      throw error;
    });
    return catalogue;
  },
  current: () => api.get<ApplicationView>('/api/applications/current'),
  create: () => api.post<ApplicationView>('/api/applications'),
  save: (id: string, request: SaveRequest) => api.patch<ApplicationView>(`/api/applications/${id}`, request),
  clear: (id: string) => api.delete<void>(`/api/applications/${id}`),
  submit: (id: string, version: number) =>
    api.post<ApplicationView>(`/api/applications/${id}/submit`, { version }),
};

/** For tests, which each start from nothing. */
export function forgetCatalogue(): void {
  catalogue = null;
}

/** Whether the applicant may still change the answers. */
export function editable(status: ApplicationStatus): boolean {
  return status === 'DRAFT' || status === 'INFORMATION_REQUESTED';
}
